import isFunction from 'lodash/isFunction';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { ReactNode } from 'react';
import type { OverlayProps } from './Overlay';
import { Overlay } from './Overlay';

export interface OverlayContainerProps<TContent> extends Pick<OverlayProps, 'overlayLabel' | 'closeLabel'> {
    content: TContent;
    openLabel: string;
}

export interface OverlayTriggerButtonProps
    extends Required<
        Pick<React.HTMLAttributes<HTMLButtonElement>, 'aria-controls' | 'aria-expanded' | 'aria-haspopup' | 'onClick'>
    > {
    'aria-haspopup': 'dialog';
    onClick: () => void; // Overriden base 'onClick' to not expose the event
}

export interface WithOverlayProps extends Pick<OverlayProps, 'overlayLabel' | 'closeLabel' | 'className'> {
    overlayContent: ((close: () => void, isOpen: boolean) => ReactNode) | ReactNode;
    children: (triggerButtonProps: OverlayTriggerButtonProps) => ReactNode;
}

export const WithOverlay: React.FC<WithOverlayProps> = props => {
    const { overlayContent, children, ...restProps } = props;

    const [isActive, setIsActive] = React.useState(false);

    const overlayId = uniqueId('overlay_');

    const triggerButtonProps: OverlayTriggerButtonProps = {
        onClick: () => setIsActive(true),
        'aria-expanded': isActive,
        'aria-controls': overlayId,
        'aria-haspopup': 'dialog',
    };

    const close = () => setIsActive(false);

    return (
        <>
            {children(triggerButtonProps)}
            <Overlay id={overlayId} isActive={isActive} close={close} {...restProps}>
                {isFunction(overlayContent) ? overlayContent(close, isActive) : overlayContent}
            </Overlay>
        </>
    );
};
